import { createEffect, createSignal, type Signal } from "solid-js";

export type PersistStorage = "local" | "session";

const DEFAULT_STORAGE = "local";

/**
 * creates a signal that persists its value to storage
 *
 * @param options.key - unique identifier for the value in storage
 * @param options.storage - storage type to use for the value
 * @param options.initialValue - if defined will be used as the initial value (helpful for passing in data from query params)
 * @param options.fallbackValue - fallback value to use if no value is stored
 * @returns signal
 */
export function createStoredSignal<T = unknown>(
  options:
    | {
        key: string;
        storage?: PersistStorage;
        initialValue: T | undefined;
        fallbackValue: T;
      }
    | {
        key: string;
        storage?: PersistStorage;
        fallbackValue: T;
      },
): Signal<T> {
  const persistedKey = makeKey(options.key);
  const storage = getStorage(options.storage ?? DEFAULT_STORAGE);

  const [state, setState] = createSignal<T>(
    "initialValue" in options
      ? getInitialValue(options.initialValue, options.fallbackValue, persistedKey, storage)
      : getInitialValue(undefined, options.fallbackValue, persistedKey, storage),
  );

  createEffect(() => {
    storeValue(persistedKey, state(), storage);
  });

  return [state, setState];
}

function getInitialValue<T = unknown>(
  initialValue: T | undefined,
  fallbackValue: T,
  key: string,
  storage: Storage | null,
): T {
  // first try to use initial state
  if (initialValue !== undefined) {
    return initialValue;
  }
  // then try to use persisted value — storage may be null or throw inside third-party
  // iframes / private mode, so fall back gracefully rather than crashing render
  try {
    const stringValue = storage?.getItem(key);
    if (stringValue) {
      return JSON.parse(stringValue) as T;
    }
  } catch {
    // unreadable storage — use fallback
  }
  // then use fallback state
  return fallbackValue;
}

function storeValue(key: string, value: unknown, storage: Storage | null) {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    // unwritable storage (blocked, full, or unavailable) — skip persistence
  }
}

function getStorage(storage: "local" | "session"): Storage | null {
  try {
    return storage === "local" ? localStorage : sessionStorage;
  } catch {
    return null;
  }
}

function makeKey(key: string) {
  return `use-persist:${key}`;
}

export function createLocalUserSetting<T = unknown>(options: {
  userId: string;
  key: string;
  fallbackValue: T;
}): Signal<T> {
  return createStoredSignal({
    key: `user-setting:${options.userId}:${options.key}`,
    storage: "local",
    fallbackValue: options.fallbackValue,
  });
}
